import React, { useEffect, useState } from "react";
import { fetchProducts, mediaUrl } from "../lib/api";
import { useScrollReveal } from "../hooks/useScrollReveal";
import {
  Layers,
  ArrowUpRight,
  Check,
  Workflow,
  Sparkles,
  Bot,
  Database,
  Mail,
  X,
} from "lucide-react";

const ICON_MAP = {
  Layers,
  ArrowUpRight,
  Check,
  Workflow,
  Sparkles,
  Bot,
  Database,
  Mail,
};

const FALLBACK_PRODUCTS = [
  {
    id: "fallback-1",
    title: "Ops Autopilot",
    tagline: "Automate the boring back office",
    features: ["Invoice processing", "CRM sync", "Slack & email triage"],
    icon: "Workflow",
    order: 1,
  },
  {
    id: "fallback-mail-automation",
    title: "Mail Automation",
    tagline: "🚀 Stop Wasting Hours on Manual Emails! Send 100s of Personalized Emails in One Click Using Google Sheets or Excel — At Just ₹299!",
    features: [
      "Google Sheets & Excel sync",
      "One-click bulk personalized sending",
      "Delivery & spam optimization",
    ],
    icon: "Mail",
    order: 1,
  },
  {
    id: "fallback-2",
    title: "Growth Engine",
    tagline: "Pipeline + outreach on rails",
    features: ["Lead enrichment", "Multichannel sequences", "AI follow-ups"],
    icon: "Sparkles",
    order: 2,
  },
  {
    id: "fallback-3",
    title: "Support Copilot",
    tagline: "24/7 AI agents for customers",
    features: ["Voice + chat agents", "Knowledge base sync", "Human handoff"],
    icon: "Bot",
    order: 3,
  },
  {
    id: "fallback-4",
    title: "Data Pipelines",
    tagline: "Stream, transform, deliver",
    features: ["ETL/ELT", "Realtime dashboards", "Forecasting"],
    icon: "Database",
  },
];

const numberToWord = (num) => {
  const words = ["Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];
  return words[num] || num;
};

export const Product = ({ onOpenLead }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const revealRef = useScrollReveal(".in-view", [items]);

  useEffect(() => {
    let active = true;
    let timer;

    const load = () => {
      fetchProducts()
        .then((d) => {
          if (!active) return;
          const fetched = d && (Array.isArray(d.items) ? d.items : (Array.isArray(d) ? d : null));
          setItems(fetched && fetched.length > 0 ? fetched : FALLBACK_PRODUCTS);
          setLoading(false);
          timer = setTimeout(load, 30000); // Poll every 30s
        })
        .catch(() => {
          if (!active) return;
          setItems((prev) => (Array.isArray(prev) && prev.length > 0 ? prev : FALLBACK_PRODUCTS));
          setLoading(false);
          timer = setTimeout(load, 10000); // Retry sleeping backend in 10s
        });
    };

    load();

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, []);

  const getIconComponent = (name) => {
    return ICON_MAP[name] || Layers;
  };

  const productList = Array.isArray(items) ? items : FALLBACK_PRODUCTS;

  return (
    <div ref={revealRef} className="pt-28 pb-10" data-testid="product-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10">
        <p className="text-xs tracking-[0.3em] uppercase text-[#00F0FF] font-bold">
          Product
        </p>
        <h1 className="font-display text-5xl md:text-7xl font-black text-white mt-4 leading-[0.9] tracking-tighter">
          {numberToWord(productList.length)} <span className="text-gradient-neon">automation</span><br />engines.
        </h1>
        <p className="max-w-2xl text-zinc-400 mt-6 text-base">
          Pre-built foundations our pod customizes for your stack. We adapt,
          deploy, and operate — you stop doing the work.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-14">
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-64 rounded-3xl glass animate-pulse"
              />
            ))
            : productList.map((p, idx) => {
              const IconComponent = getIconComponent(p.icon);
              return (
                <div
                  key={p.id}
                  data-testid={`product-card-${p.id}`}
                  onClick={() => setSelectedProduct(p)}
                  className="in-view group relative glass rounded-3xl p-8 hover:border-[#00F0FF]/50 transition-colors overflow-hidden cursor-pointer"
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-[radial-gradient(circle_at_70%_0%,rgba(0,255,255,0.08),transparent_60%)] pointer-events-none" />
                  <div className="relative">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs text-zinc-500 tracking-widest">
                        0{idx + 1}
                      </span>
                      <IconComponent
                        className="w-5 h-5 text-[#00F0FF]"
                        strokeWidth={1.6}
                      />
                    </div>
                    {p.image_url && (
                      <div className="w-full aspect-video rounded-2xl overflow-hidden border border-white/10 mt-5 bg-black/40">
                        <img
                          src={mediaUrl(p.image_url)}
                          alt={p.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <h3 className="font-display text-3xl font-black text-white mt-6 tracking-tight">
                      {p.title}
                    </h3>
                    <p className="text-zinc-400 mt-1 text-sm">{p.tagline}</p>

                    <ul className="mt-6 space-y-2">
                      {p.features && Array.isArray(p.features) && p.features.map((f) => (
                        <li
                          key={f}
                          className="flex items-center gap-2 text-zinc-300 text-sm"
                        >
                          <Check className="w-4 h-4 text-[#00F0FF]" />
                          {f}
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenLead?.();
                      }}
                      data-testid={`product-cta-${p.id}`}
                      className="mt-8 inline-flex items-center gap-1 text-sm font-bold text-white group-hover:text-[#00F0FF] transition-colors"
                    >
                      Build mine <ArrowUpRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 mt-24">
        <div className="in-view glass-strong rounded-3xl p-10 md:p-14 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-display text-3xl md:text-4xl font-black text-white tracking-tight">
              Need something custom?
            </h3>
            <p className="text-zinc-400 mt-3 text-sm max-w-md">
              We've shipped everything from voice agents for clinics to data
              pipelines for hedge funds. If it can be automated, we'll build it.
            </p>
          </div>
          <div className="md:text-right">
            <button
              onClick={() => onOpenLead?.()}
              data-testid="product-page-cta"
              className="bg-[#00F0FF] text-black px-7 py-3.5 rounded-full text-sm font-bold hover:bg-[#00C8E0] transition-colors inline-flex items-center gap-2"
            >
              Talk to an engineer <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Product Details Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
            onClick={() => setSelectedProduct(null)}
          />
          <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto glass-strong rounded-3xl p-8 md:p-10 shadow-[0_0_80px_rgba(0,240,255,0.18)]">
            {/* Close Button */}
            <button
              onClick={() => setSelectedProduct(null)}
              className="absolute top-6 right-6 text-zinc-400 hover:text-white"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header Icon + Label */}
            <div className="flex items-center gap-3 mb-6">
              {(() => {
                const Icon = getIconComponent(selectedProduct.icon);
                return <Icon className="w-6 h-6 text-[#00F0FF]" />;
              })()}
              <span className="text-xs tracking-[0.2em] uppercase text-[#00F0FF] font-bold">
                Product Details
              </span>
            </div>

            {/* Title & Tagline */}
            <h3 className="font-display text-3.5xl font-black text-white mt-2 leading-tight">
              {selectedProduct.title}
            </h3>
            <p className="text-zinc-300 text-base mt-3 leading-relaxed">
              {selectedProduct.tagline}
            </p>

            {/* Large Image (if present) */}
            {selectedProduct.image_url && (
              <div className="w-full aspect-video rounded-2xl overflow-hidden border border-white/10 mt-6 bg-black/40">
                <img
                  src={mediaUrl(selectedProduct.image_url)}
                  alt={selectedProduct.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Features section */}
            <div className="mt-8 border-t border-white/10 pt-6">
              <h4 className="text-xs tracking-wider uppercase text-zinc-400 font-bold mb-4">
                Core Capabilities
              </h4>
              <ul className="space-y-3">
                {selectedProduct.features && Array.isArray(selectedProduct.features) && selectedProduct.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-3 text-zinc-300 text-sm leading-relaxed"
                  >
                    <Check className="w-4 h-4 text-[#00F0FF] mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Action button */}
            <button
              onClick={() => {
                setSelectedProduct(null);
                onOpenLead?.();
              }}
              className="w-full mt-8 bg-[#00F0FF] text-black font-bold py-4 rounded-xl hover:bg-[#00C8E0] transition-colors flex items-center justify-center gap-2"
            >
              Build mine <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Product;
